import { useEffect, useState } from "react";
import {
  Phone,
  PhoneOff,
  PhoneCall,
  Radio,
  CheckCircle2,
  XCircle,
  RotateCcw,
  ChevronLeft,
  Sparkles,
  ListChecks,
  ShieldCheck,
  Eye,
  Award,
  AlertTriangle,
  Clock,
  CalendarCheck,
  Home,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FurtherReading } from "@/components/FurtherReading";

/* ---------- top status bar: red-flag radar ---------- */
export const RadarPill = ({ caught, total }) => (
  <div
    className="flex items-center gap-2 text-xs font-semibold text-slate-500"
    data-testid="radar-pill"
  >
    <Radio className="w-4 h-4 text-[#0047AB]" />
    <span className="uppercase tracking-wider">Red-Flag Radar</span>
    <span className="font-mono text-[#0047AB]" data-testid="radar-count">
      {caught}/{total} caught
    </span>
  </div>
);

/* ---------- live "speaking" audio waveform ---------- */
const WAVE_BARS = Array.from({ length: 30 }, (_, i) => ({
  delay: (i * 67) % 900,
  dur: 700 + ((i * 53) % 600),
  base: 18 + ((i * 37) % 64),
}));

const CallWaveform = () => (
  <div className="flex items-end justify-center gap-[3px] h-9" data-testid="call-waveform" aria-hidden="true">
    {WAVE_BARS.map((b, i) => (
      <span
        key={i}
        className="call-bar w-[3px] rounded-full bg-emerald-400/80"
        style={{ height: `${b.base}%`, animationDelay: `${b.delay}ms`, animationDuration: `${b.dur}ms` }}
      />
    ))}
  </div>
);

const fmtClock = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

/* ---------- dark caller console (left) ---------- */
export const CallConsole = ({ scenario }) => {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    setSecs(0);
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [scenario]);

  return (
    <div
      className="rounded-2xl p-5 text-slate-200 shadow-lg bg-[#0b1726] border border-[#1d3350]"
      data-testid="call-console"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] font-mono tracking-wider text-emerald-400">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          LIVE · LINE 1
        </div>
        <span className="text-[10px] font-mono text-rose-400 tracking-widest">REC ●</span>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <div className="w-10 h-10 rounded-full bg-[#13314a] flex items-center justify-center">
          <Phone className="w-5 h-5 text-emerald-300" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Caller</div>
          <div className="font-semibold text-slate-100" data-testid="console-caller">
            {scenario.caller}
          </div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Call time</div>
          <div className="font-mono text-emerald-300 text-sm tabular-nums" data-testid="call-timer">
            {fmtClock(secs)}
          </div>
        </div>
      </div>

      {/* animated speaking waveform */}
      <div className="mt-3 rounded-lg bg-[#081320] border border-[#142840] px-3 py-2">
        <CallWaveform />
        <div className="text-[9px] font-mono text-center text-slate-500 tracking-widest mt-1">CALLER SPEAKING…</div>
      </div>

      <dl className="mt-4 space-y-2 text-sm">
        {[
          ["Patient", scenario.patient],
          ["Signalment", scenario.signalment],
          ["Demeanour", scenario.demeanour],
          ["Summary", scenario.summary],
        ].map(([k, v]) => (
          <div key={k} className="grid grid-cols-[88px_1fr] gap-2">
            <dt className="text-[11px] uppercase tracking-wider text-slate-500 pt-0.5">{k}</dt>
            <dd className="text-slate-200 leading-snug">{v}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-4 pt-3 border-t border-[#1d3350]">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Operator brief</div>
        <p className="text-[12px] text-emerald-200/80 leading-relaxed">
          Listen to the caller. Tap any phrase in their words that you recognise as a red flag — the
          line locks the moment you reply. Then pick the best response on the right.
        </p>
      </div>
    </div>
  );
};

/* ---------- transcript with inline tappable red-flag phrases ---------- */
const slug = (t) => t.slice(0, 18).replace(/[^a-z0-9]+/gi, "-").toLowerCase();

// Split a caller line into plain segments and tappable red-flag/decoy phrases.
function segmentLine(text, chips) {
  const marks = [];
  (chips || []).forEach((c) => {
    const idx = text.indexOf(c.t);
    if (idx >= 0) marks.push({ start: idx, end: idx + c.t.length, chip: c });
  });
  marks.sort((a, b) => a.start - b.start);
  const clean = [];
  let lastEnd = -1;
  marks.forEach((m) => {
    if (m.start >= lastEnd) {
      clean.push(m);
      lastEnd = m.end;
    }
  });
  const segs = [];
  let pos = 0;
  clean.forEach((m) => {
    if (m.start > pos) segs.push({ text: text.slice(pos, m.start), key: `t${pos}` });
    segs.push({ text: text.slice(m.start, m.end), chip: m.chip, key: `c${m.start}` });
    pos = m.end;
  });
  if (pos < text.length) segs.push({ text: text.slice(pos), key: `t${pos}` });
  return segs;
}

const CallerText = ({ text, chips, active, tapped, caught, onTap }) => {
  const segs = segmentLine(text, chips);
  return (
    <span>
      {segs.map((s) => {
        if (!s.chip) return <span key={s.key}>{s.text}</span>;
        const isTapped = tapped.has(s.chip.t);
        const isCaught = caught.has(s.chip.t);
        if (isTapped)
          return (
            <span
              key={s.key}
              className={
                isCaught
                  ? "bg-emerald-200/80 text-emerald-900 rounded px-0.5 font-medium"
                  : "bg-rose-200/80 text-rose-700 rounded px-0.5 line-through"
              }
            >
              {s.text}
            </span>
          );
        if (active)
          return (
            <span
              key={s.key}
              role="button"
              tabIndex={0}
              onClick={() => onTap(s.chip)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onTap(s.chip);
                }
              }}
              data-testid={`flag-span-${slug(s.chip.t)}`}
              className="cursor-pointer rounded px-0.5 hover:bg-[#0047AB]/5 transition-colors"
            >
              {s.text}
            </span>
          );
        // locked & untapped — render plain (missed flag is revealed only in the debrief)
        return <span key={s.key}>{s.text}</span>;
      })}
    </span>
  );
};

export const Transcript = ({ history, turn, total, onTap, tapped, caught }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
          <Radio className="w-3.5 h-3.5" /> Transcript
        </div>
        <div className="text-[11px] font-mono text-slate-400" data-testid="turn-counter">
          Turn {turn} / {total}
        </div>
      </div>

      <div className="space-y-3" data-testid="transcript">
        {history.map((e) => {
          if (e.role === "narration")
            return (
              <div key={e.id} className="text-[11px] font-mono uppercase tracking-wide text-slate-400">
                {e.text}
              </div>
            );
          if (e.role === "you" || e.role === "triage")
            return (
              <div key={e.id} className="flex justify-end">
                <div
                  className={`max-w-[80%] rounded-2xl rounded-br-sm px-4 py-2.5 text-sm font-medium flex items-start gap-1.5 ${
                    e.correct == null
                      ? "bg-[#0047AB] text-white"
                      : e.correct
                      ? "bg-emerald-50 text-emerald-800 border-2 border-emerald-400"
                      : "bg-rose-50 text-rose-800 border-2 border-rose-400"
                  }`}
                  data-testid={
                    e.correct == null
                      ? undefined
                      : e.correct
                      ? "response-correct"
                      : "response-incorrect"
                  }
                >
                  {e.correct != null &&
                    (e.correct ? (
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" />
                    ) : (
                      <XCircle className="w-4 h-4 mt-0.5 shrink-0 text-rose-500" />
                    ))}
                  <span>{e.role === "triage" ? e.text : `“${e.text}”`}</span>
                </div>
              </div>
            );
          // caller
          return (
            <div key={e.id}>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                {e.name}
              </div>
              <div
                className="max-w-[85%] bg-slate-100 rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm text-slate-800 leading-relaxed"
                data-testid={e.active ? "active-caller-line" : "caller-line"}
              >
                <CallerText
                  text={e.text}
                  chips={e.chips}
                  active={e.active}
                  tapped={tapped}
                  caught={caught}
                  onTap={onTap}
                />
              </div>
              {e.active && e.chips && e.chips.length > 0 && (
                <div className="text-[11px] text-slate-400 mt-1.5 italic">
                  Tap any phrase you recognise as a red flag — they lock once you reply.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </CardContent>
  </Card>
);

/* ---------- decision panel (A/B/C) ---------- */
const optLetter = ["A", "B", "C", "D"];
export const DecisionPanel = ({ beat, onPick }) => (
  <Card className="border-slate-200" data-testid="decision-panel">
    <CardContent className="pt-5">
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
        Your turn · <span className="text-[#0047AB]">{beat.category}</span>
      </div>
      <div className="text-base font-bold text-slate-900 mt-1 mb-4">{beat.prompt}</div>
      <div className="space-y-2.5">
        {beat.options.map((o, i) => (
          <button
            key={o.t}
            type="button"
            onClick={() => onPick(i)}
            data-testid={`option-${i}`}
            className="w-full text-left flex items-start gap-3 border-2 border-slate-200 rounded-md px-4 py-3 hover:border-[#0047AB] hover:bg-[#0047AB]/5 transition-colors"
          >
            <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full border border-slate-300 text-[10px] font-bold flex items-center justify-center text-slate-500">
              {optLetter[i]}
            </span>
            <span className="text-sm text-slate-800">“{o.t}”</span>
          </button>
        ))}
      </div>
    </CardContent>
  </Card>
);

/* ---------- final triage selection (traffic-light dial) ---------- */
const urgencyStyle = (t) => {
  if (/emergency/i.test(t)) return { color: "#ef4444", lamp: 0, ring: "hover:border-rose-400 hover:bg-rose-50", Icon: AlertTriangle, tag: "Emergency" };
  if (/urgent/i.test(t)) return { color: "#f59e0b", lamp: 1, ring: "hover:border-amber-400 hover:bg-amber-50", Icon: Clock, tag: "Urgent" };
  if (/routine|book/i.test(t)) return { color: "#10b981", lamp: 2, ring: "hover:border-emerald-400 hover:bg-emerald-50", Icon: CalendarCheck, tag: "Routine" };
  return { color: "#0ea5e9", lamp: -1, ring: "hover:border-sky-400 hover:bg-sky-50", Icon: Home, tag: "Advice & monitor" };
};

const LAMPS = ["#ef4444", "#f59e0b", "#10b981"];

export const TriagePanel = ({ triage, onPick }) => (
  <Card className="border-slate-200" data-testid="triage-panel">
    <CardContent className="pt-5">
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
        Your turn · <span className="text-[#0047AB]">SET THE URGENCY DIAL</span>
      </div>
      <div className="text-base font-bold text-slate-900 mt-1 mb-4">{triage.prompt}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {triage.options.map((o, i) => {
          const s = urgencyStyle(o.t);
          return (
            <button
              key={o.t}
              type="button"
              onClick={() => onPick(i)}
              data-testid={`triage-option-${i}`}
              className={`flex items-center gap-3 text-left border-2 border-slate-200 rounded-lg px-4 py-3 transition-colors ${s.ring}`}
            >
              {/* traffic-light housing */}
              <div className="flex flex-col gap-1 bg-slate-800 rounded-md p-1 shrink-0">
                {LAMPS.map((c, li) => {
                  const lit = s.lamp === li;
                  return (
                    <span
                      key={li}
                      className="w-3 h-3 rounded-full transition-all"
                      style={{
                        background: lit ? c : "#1e293b",
                        boxShadow: lit ? `0 0 8px ${c}` : "none",
                        opacity: lit ? 1 : 0.5,
                      }}
                    />
                  );
                })}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold" style={{ color: s.color }}>
                  <s.Icon className="w-3.5 h-3.5" /> {s.tag}
                </div>
                <div className="text-sm font-semibold text-slate-800 leading-snug">{o.t}</div>
              </div>
            </button>
          );
        })}
      </div>
    </CardContent>
  </Card>
);

/* ---------- completion gate ---------- */
export const CompleteGate = ({ onSubmit, submitting }) => (
  <Card className="border-slate-200" data-testid="complete-gate">
    <CardContent className="pt-6 pb-6 flex flex-col items-center text-center">
      <PhoneOff className="w-7 h-7 text-slate-400" />
      <div className="text-lg font-bold text-slate-900 mt-2">Call complete</div>
      <p className="text-sm text-slate-500 mt-1 max-w-xs">
        Submit your call to unlock the Red-Flag Radar score and assessor coaching.
      </p>
      <Button
        onClick={onSubmit}
        disabled={submitting}
        data-testid="submit-feedback-btn"
        className="mt-4 bg-[#0047AB] hover:bg-[#003380] text-white"
      >
        <Sparkles className="w-4 h-4 mr-1.5" />
        {submitting ? "Grading…" : "Submit & see feedback"}
      </Button>
    </CardContent>
  </Card>
);

/* ---------- feedback / grade screen ---------- */
const toneText = { good: "text-emerald-600", warn: "text-amber-600", bad: "text-rose-600" };
const toneBadge = {
  good: "bg-emerald-100 text-emerald-700",
  warn: "bg-amber-100 text-amber-700",
  bad: "bg-rose-100 text-rose-700",
};

export const Feedback = ({ result, onNext, nextLabel, onHome }) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-testid="feedback">
    {/* score card */}
    <div className="space-y-4">
      <div className="rounded-2xl p-5 bg-[#0b1726] border border-[#1d3350] text-slate-200 shadow-lg">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">
          Call closed · grade ready
        </div>
        <div className={`text-5xl font-bold mt-1 ${toneText[result.verdict.tone]}`} data-testid="grade-pct">
          {result.score}
          <span className="text-2xl">%</span>
        </div>
        <div className="text-xs text-slate-400 font-mono">{result.score} / 100 pts</div>
        <span
          className={`inline-block mt-2 text-[11px] font-bold px-2.5 py-1 rounded ${toneBadge[result.verdict.tone]}`}
          data-testid="verdict"
        >
          {result.verdict.label}
        </span>

        <dl className="mt-4 pt-3 border-t border-[#1d3350] space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <dt className="text-slate-400">Triage</dt>
            <dd className={result.triageCorrect ? "text-emerald-400 font-semibold" : "text-rose-400 font-semibold"}>
              {result.triageCorrect ? "Correct" : "Review"}
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-slate-400">Red flags</dt>
            <dd className="text-slate-200 font-mono">
              {result.caughtCount} / {result.totalFlags} caught
            </dd>
          </div>
        </dl>
      </div>
      <div className="flex gap-2">
        <Button onClick={onNext} data-testid="replay-btn" className="flex-1 bg-[#0047AB] hover:bg-[#003380] text-white">
          <RotateCcw className="w-4 h-4 mr-1.5" /> {nextLabel}
        </Button>
        <Button variant="outline" onClick={onHome} data-testid="feedback-home-btn" className="text-slate-600">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Simulator Hub
        </Button>
      </div>
    </div>

    {/* coaching + rubric */}
    <div className="lg:col-span-2 space-y-5">
      <Card className="border-slate-200">
        <CardContent className="pt-5">
          <div className="text-[11px] uppercase tracking-wider text-[#0047AB] font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Assessor coaching
          </div>
          <h2 className="text-xl font-bold text-slate-900 mt-1">{result.title}</h2>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">{result.summary}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-emerald-600 font-semibold mb-2 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Strengths
              </div>
              <ul className="space-y-1.5">
                {result.strengths.map((s) => (
                  <li key={s} className="text-[13px] text-slate-600 flex gap-1.5">
                    <span className="text-emerald-500">›</span> {s}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-amber-600 font-semibold mb-2 flex items-center gap-1.5">
                <ListChecks className="w-3.5 h-3.5" /> Improvements
              </div>
              <ul className="space-y-1.5">
                {result.improvements.map((s) => (
                  <li key={s} className="text-[13px] text-slate-600 flex gap-1.5">
                    <span className="text-amber-500">›</span> {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="pt-5">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Rubric breakdown
          </div>
          <div className="space-y-4">
            {result.rubric.map((r, i) => (
              <div key={r.label} data-testid={`rubric-${i}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-800">{r.label}</span>
                  <span className="font-mono text-slate-500">
                    {r.got} / {r.max}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1.5">
                  <div
                    className="h-full bg-[#0047AB] transition-[width] duration-500"
                    style={{ width: `${(r.got / r.max) * 100}%` }}
                  />
                </div>
                <div className="text-[11px] text-slate-400 mt-1">{r.note}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="pt-5">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3 flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" /> Red-Flag Radar · revealed
          </div>
          {result.revealed.length === 0 ? (
            <div className="text-sm text-slate-400">No red flags in this call — a benign presentation.</div>
          ) : (
            <ul className="space-y-2">
              {result.revealed.map((f, i) => (
                <li key={f.phrase} className="flex items-start gap-2 text-sm" data-testid={`revealed-${i}`}>
                  {f.caught ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />
                  )}
                  <span className={f.caught ? "text-slate-600" : "text-slate-500"}>
                    <span className="font-semibold">{f.caught ? "Caught" : "Missed"}:</span> “{f.phrase}”
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <FurtherReading topic="triage" />
    </div>
  </div>
);


/* ---------- aggregated shift summary (3 calls) ---------- */
const verdictOf = (avg) =>
  avg >= 85
    ? { label: "Competent", tone: "good" }
    : avg >= 70
    ? { label: "Needs revision", tone: "warn" }
    : { label: "Not yet competent", tone: "bad" };

export const ShiftSummary = ({ results, onNewShift, onHome }) => {
  const avg = Math.round(results.reduce((a, r) => a + r.score, 0) / results.length);
  const verdict = verdictOf(avg);
  const triageCorrect = results.filter((r) => r.triageCorrect).length;
  const flagsCaught = results.reduce((a, r) => a + r.caughtCount, 0);
  const flagsTotal = results.reduce((a, r) => a + r.totalFlags, 0);
  const improvements = [...new Set(results.flatMap((r) => r.improvements))].slice(0, 5);
  const strengths = [...new Set(results.flatMap((r) => r.strengths))].slice(0, 4);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-testid="shift-summary">
      {/* shift score card */}
      <div className="space-y-4">
        <div className="rounded-2xl p-5 bg-[#0b1726] border border-[#1d3350] text-slate-200 shadow-lg">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
            <Award className="w-3.5 h-3.5" /> Shift complete · {results.length} calls
          </div>
          <div className={`text-5xl font-bold mt-1 ${toneText[verdict.tone]}`} data-testid="shift-avg">
            {avg}
            <span className="text-2xl">%</span>
          </div>
          <div className="text-xs text-slate-400 font-mono">shift average</div>
          <span
            className={`inline-block mt-2 text-[11px] font-bold px-2.5 py-1 rounded ${toneBadge[verdict.tone]}`}
            data-testid="shift-verdict"
          >
            {verdict.label}
          </span>
          <dl className="mt-4 pt-3 border-t border-[#1d3350] space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-slate-400">Triage correct</dt>
              <dd className="text-slate-200 font-mono">
                {triageCorrect} / {results.length}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-slate-400">Red flags</dt>
              <dd className="text-slate-200 font-mono">
                {flagsCaught} / {flagsTotal} caught
              </dd>
            </div>
          </dl>
        </div>
        <div className="flex gap-2">
          <Button onClick={onNewShift} data-testid="new-shift-btn" className="flex-1 bg-[#0047AB] hover:bg-[#003380] text-white">
            <PhoneCall className="w-4 h-4 mr-1.5" /> New shift
          </Button>
          <Button variant="outline" onClick={onHome} data-testid="shift-home-btn" className="text-slate-600">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Simulator Hub
          </Button>
        </div>
      </div>

      {/* per-call breakdown + themes */}
      <div className="lg:col-span-2 space-y-5">
        <Card className="border-slate-200">
          <CardContent className="pt-5">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3 flex items-center gap-1.5">
              <Radio className="w-3.5 h-3.5" /> Calls this shift
            </div>
            <div className="space-y-2.5">
              {results.map((r, i) => (
                <div
                  key={r.title}
                  className="flex items-center gap-3 border border-slate-200 rounded-md px-4 py-3"
                  data-testid={`shift-call-${i}`}
                >
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-500 shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{r.title}</div>
                    <div className="text-[11px] text-slate-500">
                      {r.triageCorrect ? "Triage correct" : "Triage review"} · {r.caughtCount}/
                      {r.totalFlags} flags
                    </div>
                  </div>
                  <span className={`text-lg font-bold ${toneText[r.verdict.tone]}`}>{r.score}%</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${toneBadge[r.verdict.tone]}`}>
                    {r.verdict.label}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-5">
            <div className="text-[11px] uppercase tracking-wider text-[#0047AB] font-semibold flex items-center gap-1.5 mb-3">
              <Sparkles className="w-3.5 h-3.5" /> Shift debrief
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-600 font-semibold mb-2 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> What went well
                </div>
                <ul className="space-y-1.5">
                  {strengths.map((s) => (
                    <li key={s} className="text-[13px] text-slate-600 flex gap-1.5">
                      <span className="text-emerald-500">›</span> {s}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-amber-600 font-semibold mb-2 flex items-center gap-1.5">
                  <ListChecks className="w-3.5 h-3.5" /> Focus for next shift
                </div>
                <ul className="space-y-1.5">
                  {improvements.map((s) => (
                    <li key={s} className="text-[13px] text-slate-600 flex gap-1.5">
                      <span className="text-amber-500">›</span> {s}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <FurtherReading topic="triage" />
      </div>
    </div>
  );
};
